import React, { useEffect, useRef, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { IVisitor } from './types';
import { useSignalR } from '../../signalR';
import { useAuthentication } from '../../authentiction';
import { useDataFetcher } from '../../hooks';
import { useInterval } from 'beautiful-react-hooks';
import { ReceptionSignIn } from '../signIn';
import { ReceptionListPage } from './ReceptionListPage';
import { ReceptionListFilterBar } from './ReceptionListFilterBar';
import BadgeListPage from '../badges/list';
import RegisterBadgePage from '../badges/register';
import VisitorSummaryPage from '../visitorSummary';
import NotificationService from '../../../services/NotificationService';
import byTimes from './byTimes';
import toISODateOnlyString from '../../../utils/toISODateOnlyString';
import { Container } from 'react-bootstrap';
import { FormHeading } from '../../form/formHeading';

const transformVisitor = (visitor: IVisitor): IVisitor => ({
  ...visitor,
  inAt: new Date(visitor.inAt),
  outAt: visitor.outAt ? new Date(visitor.outAt) : null,
});

const transformData = (data: { visitors: Array<IVisitor> }): Array<IVisitor> =>
  data.visitors.map(transformVisitor);

const config = { transformer: transformData };

export const ReceptionContainer: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    toISODateOnlyString(new Date()),
  );
  const [visitorTypeId, setVisitorTypeId] = useState<string>('');
  const [isToday, setIsToday] = useState<boolean>(true);
  const [tableMode, setTableMode] = useState<boolean>(false);
  const [showSignedOutVisitors, setShowSignedOutVisitors] =
    useState<boolean>(true);

  const notificationService = useRef<NotificationService>(
    new NotificationService(),
  );
  const { connected, subscribe, unsubscribe } = useSignalR();
  const { pin, onLogout } = useAuthentication();

  const { busy, data, errors } = useDataFetcher(
    `/api/sites/${pin}/visitors?date=${selectedDate}`,
    config,
  );

  const [visitors, setVisitors] = useState<Array<IVisitor>>([]);

  useInterval(() => {
    if (isToday && selectedDate !== toISODateOnlyString(new Date())) {
      setSelectedDate(toISODateOnlyString(new Date()));
    }
  }, 30000);

  // today
  useEffect(() => {
    setIsToday(selectedDate === toISODateOnlyString(new Date()));
  }, [selectedDate]);

  // notifications
  useEffect(() => {
    notificationService.current.requestPermission();
  }, []);

  // visitors
  useEffect(() => {
    if (data) {
      setVisitors(data);
    }
  }, [data]);

  // subscribe to SignalR messages
  useEffect(() => {
    const handleSignInMessageReceived = (
      role: string,
      visitor: IVisitor,
    ): void => {
      if (isToday) {
        setVisitors([...visitors, transformVisitor(visitor)].sort(byTimes));
      }

      notificationService.current.send(
        `${visitor.firstNames} ${visitor.surname} is here to see ${visitor.host}.`,
      );
    };

    const handleSignOutMessageReceived = (
      role: string,
      dto: { id: number; firstNames: string; surname: string; outAt: string },
    ): void => {
      if (isToday) {
        const index = visitors.findIndex((visitor) => visitor.id === dto.id);

        if (index !== -1) {
          setVisitors([
            ...visitors.slice(0, index),
            Object.assign({}, visitors[index], { outAt: new Date(dto.outAt) }),
            ...visitors.slice(index + 1),
          ]);
        }
      }

      notificationService.current.send(
        `${dto.firstNames} ${dto.surname} has signed out.`,
      );
    };

    const handleAutoSignOutOccurredMessageReceived = (
      sitePin: string,
      dto: { outAt: string; visitors: Array<number> },
    ): void => {
      if (sitePin !== pin || dto.visitors.length === 0) return;

      setVisitors(
        visitors.map((visitor: IVisitor) =>
          dto.visitors.indexOf(visitor.id) === -1
            ? visitor
            : { ...visitor, outAt: new Date(dto.outAt) },
        ),
      );

      let message = `${dto.visitors.length} visitors were automatically signed out.`;

      if (dto.visitors.length === 1) {
        message = 'One visitor was automatically signed out';
      }
      notificationService.current.send(message);
    };

    const handleSiteDeactivatedMessageReceived = (sitePin: string) => {
      if (sitePin === pin) onLogout();
    };

    if (connected) {
      subscribe('ReceiveSignInMessage', handleSignInMessageReceived);
      subscribe('ReceiveSignOutMessage', handleSignOutMessageReceived);
      subscribe(
        'ReceiveAutoSignOutOccurredMessage',
        handleAutoSignOutOccurredMessageReceived,
      );
      subscribe(
        'ReceiveSiteDeactivatedMessage',
        handleSiteDeactivatedMessageReceived,
      );

      return () => {
        unsubscribe('ReceiveSignInMessage');
        unsubscribe('ReceiveSignOutMessage');
        unsubscribe('ReceiveAutoSignOutOccurredMessage');
        unsubscribe('ReceiveSiteDeactivatedMessage');
      };
    }
  }, [connected, subscribe, unsubscribe, pin, visitors, isToday, onLogout]);

  return (
    <Switch>
      <Route path="/reception/signIn" component={ReceptionSignIn} />
      <Route
        path="/reception/visitor/:visitorId"
        component={VisitorSummaryPage}
      />
      <Route path="/reception/badges/register" component={RegisterBadgePage} />
      <Route path="/reception/badges" exact component={BadgeListPage} />
      <Route
        path="/"
        render={() => (
          <Container fluid>
            <FormHeading title="Visitors" subTitle="List" />

            <ReceptionListFilterBar
              selectedDate={selectedDate}
              visitorTypeId={visitorTypeId}
              isToday={isToday}
              tableMode={tableMode}
              showSignedOutVisitors={showSignedOutVisitors}
              onDateChange={setSelectedDate}
              onVisitorTypeIdChange={setVisitorTypeId}
              onTableModeChanged={setTableMode}
              onShowSignedOutVisitorsChange={setShowSignedOutVisitors}
            />
            <ReceptionListPage
              busy={busy}
              errors={errors}
              visitors={visitors}
              visitorTypeId={visitorTypeId}
              tableMode={tableMode}
              showSignedOutVisitors={showSignedOutVisitors}
              isToday={isToday}
            />
          </Container>
        )}
      />
    </Switch>
  );
};
